import { useEffect, useRef } from 'react';

import { useButtonPressedCallback } from '@zenbusiness/application-commons-clickstream';
import { WebView } from '@zenbusiness/application-commons-webview';
import Box from '@zenbusiness/zui/Box';
import Divider from '@zenbusiness/zui/Divider';
import Heading from '@zenbusiness/zui/Heading';
import Link from '@zenbusiness/zui/Link';
import LoadingButton from '@zenbusiness/zui/LoadingButton';
import Paper from '@zenbusiness/zui/Paper';
import SharedAsset from '@zenbusiness/zui/SharedAsset';
import Stack from '@zenbusiness/zui/Stack';
import { styled } from '@zenbusiness/zui/styles';
import Text from '@zenbusiness/zui/Text';
import PropTypes from 'prop-types';
import { useReactToPrint } from 'react-to-print';

import { ActionButtonsGrid, FocusGrid, ContainerGrid } from '#client/components/Grid';
import { ACTION_BUTTON_TYPE } from '#client/components/Grid/constants';
import CardDetail from '#client/components/MoveMoney/components/SummaryFormComponents/CardDetail';
import InstantDepositDetail from '#client/components/MoveMoney/components/SummaryFormComponents/InstantDepositDetail';
import RecipientDetail from '#client/components/MoveMoney/components/SummaryFormComponents/RecipientDetail';
import { convertCentToUSDText } from '#client/components/MoveMoney/utils/common';
import { INSTANT_DEPOSIT_SUCCESS_SURVEY } from '#client/constants/hotjarEvents';
import { PHONE_NUMBER } from '#client/constants/phone';
import useFormStep from '#client/hooks/useFormStep';
import { useHotJarEvent } from '#client/hooks/useHotJar';
import useModalContext from '#client/hooks/useModalContext';
import useModalDisplayTracker from '#client/hooks/useModalDisplayTracker';
import { capitalizeFirstLetter, formatAccountNumber } from '#client/utils/text';

import { DialogContentStyled } from './DialogContent.styles';
import { PrintedBoxStyle } from './SuccessForm.styles';
import { SummaryFormWrapperStyled } from './SummaryFormWrapper.styles';

import { INSTANT_TRANSFER_STATUS } from '../constants/instants';

const PrintedBox = styled(Box)(PrintedBoxStyle);
const checkIcon = (
  <SharedAsset
    alt=""
    color="#33F28B"
    height={50}
    src="/s2/icon/check-circle-material-fill.svg"
    width={50}
  />
);

export default function SuccessForm({ title, transactionTitle, transactionSubTitle, handleClose }) {
  const sendHotjarEvent = useHotJarEvent();
  const { isWebView } = WebView;
  const { depositData } = useFormStep();
  const { clearAllModal } = useModalContext();

  const { selectedFromAccount, amount, toAccount, initialDate } = depositData;

  const { cardHolderFirstName, cardHolderLastName, cardLastFourDigits, cardType } =
    selectedFromAccount;
  const status =
    depositData?.transferStatus === INSTANT_TRANSFER_STATUS.PROCESSED ? 'Complete' : 'Processing';
  const fromCardHolder = [cardHolderFirstName, cardHolderLastName].join(' ');
  const fromType = capitalizeFirstLetter(cardType);
  const fromCardNumber = formatAccountNumber({
    accountNumber: cardLastFourDigits,
    formatCharacter: '•',
    numberFormatCharacter: 2
  });

  const amountInUSD = convertCentToUSDText(amount);
  const toName = toAccount?.name ?? '';

  const buttonClosePressed = useButtonPressedCallback({
    elementName: 'close_confirm_instantdep',
    elementText: 'Close',
    appPageName: 'transaction_confirmed_instantdep_modal'
  });

  const handleCloseModal = () => {
    buttonClosePressed();
    handleClose();
    clearAllModal();
  };

  const contentRef = useRef(null);

  const handlePrint = useReactToPrint({
    content: () => contentRef.current
  });

  useModalDisplayTracker({
    elementName: 'transaction_confirmed_instantdep_modal',
    elementText: 'SI_stripetrue_payments_screen',
    appPageName: 'active_banking_dashboard'
  });

  useEffect(() => {
    // Send Hotjar event to open survey
    sendHotjarEvent(INSTANT_DEPOSIT_SUCCESS_SURVEY);
  }, [sendHotjarEvent]);

  return (
    <>
      <DialogContentStyled>
        <ContainerGrid>
          <FocusGrid>
            <SummaryFormWrapperStyled role="form">
              <PrintedBox ref={contentRef}>
                <Stack
                  alignItems="center"
                  flexDirection={{ xs: 'column', lg: 'row' }}
                  gap="8px"
                  justifyContent="center"
                  paddingBottom={{ xs: '32px', lg: '40px' }}
                >
                  {checkIcon}
                  <Heading
                    align="center"
                    size="3xl"
                    weight="bold"
                  >
                    {title}
                  </Heading>
                </Stack>
                <Paper variant="outlined">
                  <InstantDepositDetail
                    amount={amountInUSD}
                    dateInitiated={initialDate}
                    status={status}
                    subTitle={transactionSubTitle}
                    title={transactionTitle}
                    transactionType="Debit Card Transaction"
                    transferType="Instant deposit"
                  />
                  <Divider />
                  <RecipientDetail
                    accountHolder={toName}
                    accountNumber={toAccount?.accountNumber}
                    bank="ZenBusiness"
                    fullAccountNumber={toAccount?.accountNumber}
                    title="Recipient"
                  />
                  <Divider />
                  <CardDetail
                    cardHolder={fromCardHolder}
                    cardNumber={fromCardNumber}
                    type={fromType}
                  />
                </Paper>
              </PrintedBox>
              <Box mt={{ xs: '32px', lg: '40px' }}>
                {!isWebView && (
                  <>
                    <Text
                      color="tertiary"
                      size="sm"
                    >
                      All transactions are subject to review. It is recommended that you{' '}
                      <Text
                        component="span"
                        onClick={handlePrint}
                        size="sm"
                      >
                        print this page
                      </Text>{' '}
                      for your records.
                    </Text>
                    <br />
                  </>
                )}
                <Text
                  color="primary"
                  size="sm"
                  weight="medium"
                >
                  Notice a problem? Call{' '}
                  <Link
                    color="info"
                    href={`tel:${PHONE_NUMBER}`}
                    size="sm"
                    underline="none"
                    weight="medium"
                  >
                    {PHONE_NUMBER}
                  </Link>
                </Text>
              </Box>
            </SummaryFormWrapperStyled>
          </FocusGrid>
        </ContainerGrid>
      </DialogContentStyled>
      <ActionButtonsGrid type={ACTION_BUTTON_TYPE.TERTIARY}>
        <LoadingButton
          color="primary"
          fullWidth
          onClick={handleCloseModal}
          size="large"
          variant="contained"
        >
          Close
        </LoadingButton>
      </ActionButtonsGrid>
    </>
  );
}

SuccessForm.propTypes = {
  title: PropTypes.string.isRequired,
  transactionTitle: PropTypes.string.isRequired,
  transactionSubTitle: PropTypes.string.isRequired,
  handleClose: PropTypes.func.isRequired
};
